import { ChordLib } from '@tunasong/music-lib'
import type { Chord, CadenceOption } from '@tunasong/music-lib'
import type { FC } from 'react'
import { useState } from 'react'
import { TheoryGlyphs } from '../theory-copy/theory-glyphs.js'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import { TheoryCard } from '../window-tag/theory-card.js'
import { useCadenceLabPlayback } from './cadence-lab-playback.js'
import styles from './cadence-option-card.module.css'

export type CadenceCardKey = 'major' | 'minor'

/**
 * Bowie's §8 Q5 suppression captions, level-resolved. `{Mode}` is substituted
 * with the capitalized mode name (Mixolydian / Dorian / Phrygian).
 *
 * Lydian is handled by its own copy block — IV is diminished there, the
 * mechanism is different from the modal-no-leading-tone case.
 */
/**
 * Lexicon ids for the two suppression-reason captions. Bowie's lane —
 * pedagogical content lives in the lexicon (not inline), so the caption
 * resolves at the user's theoryLevel.
 */
const SUPPRESSION_LEXICON_ID: Record<NonNullable<CadenceOption['suppressionReason']>, string> = {
  'modal-no-leading-tone': 'cadence-suppressed-no-leading-tone',
  'lydian-diminished-iv': 'cadence-suppressed-lydian-iv',
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

export interface CadenceOptionCardProps {
  option: CadenceOption
  prefix: Chord[]
  /**
   * True when the user's scale is a minor KEY (`scale.type === 'minor'`).
   * Drives the Q3 deceptive-tagLabel override (V → vi → V → ♭VI) at advanced.
   */
  isMinorKey?: boolean
}

export const CadenceOptionCard: FC<CadenceOptionCardProps> = props => {
  const { option, prefix, isMinorKey } = props
  const copy = useTheoryCopy(option.lexiconId)
  const suppressionReason = option.suppressionReason ?? 'modal-no-leading-tone'
  const suppressionCopy = useTheoryCopy(SUPPRESSION_LEXICON_ID[suppressionReason])
  const [whyOpen, setWhyOpen] = useState(false)
  const { play } = useCadenceLabPlayback({
    prefix,
    endingChord: option.endingChord,
    penultSubstitution: option.penultSubstitution,
  })

  const heading = copy?.displayNameShort ?? capitalize(option.kind)

  if (!option.available) {
    // "Not available — " prefix is the consistent UI-status framing; the
    // lexicon body carries the pedagogical explanation (level-resolved).
    const caption = `Not available — ${suppressionCopy?.body ?? ''}`
    return (
      <section
        className={styles.cardDisabled}
        aria-label={`${heading} (not available)`}
        data-testid={`cadence-card-${option.kind}-disabled`}
      >
        <h3 className={styles.heading}>{heading}</h3>
        <p className={styles.suppression}>
          <TheoryGlyphs>{caption}</TheoryGlyphs>
        </p>
      </section>
    )
  }

  // Q3 deceptive override: in minor KEYS, advanced tagLabel becomes "V → ♭VI".
  const baseTag = copy?.tag ?? ''
  const tagLabel =
    option.kind === 'deceptive' && isMinorKey && copy?.level === 'advanced' ? 'V → ♭VI' : baseTag
  const endingName = option.endingChord ? ChordLib.getName(option.endingChord) : ''

  return (
    <section className={styles.card} aria-label={heading} data-testid={`cadence-card-${option.kind}`}>
      <h3 className={styles.heading}>{heading}</h3>
      <p className={styles.endingChord}>{endingName}</p>
      {tagLabel ? (
        <p className={styles.tagLabel}>
          <TheoryGlyphs>{tagLabel}</TheoryGlyphs>
        </p>
      ) : null}
      <div className={styles.actions}>
        <button type="button" aria-label="Play" onClick={play}>
          Play
        </button>
        <button
          type="button"
          aria-label="Why?"
          aria-expanded={whyOpen}
          onClick={() => setWhyOpen(o => !o)}
        >
          Why?
        </button>
      </div>
      {whyOpen ? <TheoryCard lexiconId={option.lexiconId} /> : null}
    </section>
  )
}
